import React from "react";
import { Environment, graphql, useMutation } from "react-relay";
import { EventRulesPageUpdateEventMutation as EventRulesPageUpdateEventMutationType } from "./__generated__/EventRulesPageUpdateEventMutation.graphql";
import { useIntl, FormattedMessage, defineMessage } from "react-intl";
import EditMarkdownSection from "@/components/EditMarkdownSection";
import EventAcceptRulesButton from "@/components/EventAcceptRulesButton";
import Loading from "@/components/Loading";
import { logger } from "@/common/logger";
import {
  EventRulesPageQuery$data,
  EventRulesPageQuery as EventRulesPageQueryType,
} from "./__generated__/EventRulesPageQuery.graphql";
import { LoaderArgs } from "@/utils/route";
import { preload, usePreloaded } from "@/utils/relay";
import { ActivityNotFound } from "@/components/ActivityNotFound";
const EventRulesPageUpdateEventMutation = graphql`
  mutation EventRulesPageUpdateEventMutation(
    $id: ID!
    $input: UpdateEventInput!
  ) {
    updateEvent(id: $id, input: $input) {
      node {
        id
        latestRule {
          id
          text
          entityAgreement {
            id
            createdAt
          }
        }
      }
    }
  }
`;
export default function EventRulesPage() {
  const { query } = useEventRulesPageQuery();
  if (!query.eventBySlug) {
    return <ActivityNotFound kind="event" />;
  }
  const event = query.eventBySlug;
  return <EventRuleEditMarkdownSection event={event} viewer={query.viewer} />;
}
interface EventRuleEditMarkdownSectionProps {
  event: NonNullable<EventRulesPageQuery$data["eventBySlug"]>;
  viewer: EventRulesPageQuery$data["viewer"];
}
function EventRuleEditMarkdownSection({
  event,
  viewer,
}: EventRuleEditMarkdownSectionProps) {
  const intl = useIntl();
  const [commitMutation, isMutationInFlight] =
    useMutation<EventRulesPageUpdateEventMutationType>(
      EventRulesPageUpdateEventMutation,
    );
  return (
    <EditMarkdownSection
      id={event.id}
      canEdit={event.viewerCanUpdate}
      disabled={isMutationInFlight}
      content={event.latestRule.text}
      title={
        <div className="flex flex-row space-x-5">
          <div className="grow">
            <h1 className="text-2xl font-bold w-full font-poppins">
              <FormattedMessage defaultMessage="Rules" />
            </h1>
            <p className="py-2">
              <FormattedMessage defaultMessage="By default all events must comply to the rules defined in" />{" "}
              <a
                href="https://aqora.io/terms"
                target="_blank"
                rel="noreferrer"
                className="text-blue-400"
              >
                <FormattedMessage defaultMessage="aqora's Terms of Use" />
              </a>
            </p>
          </div>
          <div className="self-center">
            {viewer && event.viewerCanAccept && (
              <React.Suspense fallback={<Loading className="w-10" />}>
                <EventAcceptRulesButton event={event} />
              </React.Suspense>
            )}
          </div>
        </div>
      }
      onSubmit={({ content }) =>
        new Promise<void>((resolve, reject) =>
          commitMutation({
            variables: {
              id: event.id,
              input: { rules: content || null },
            },
            onCompleted: () => resolve(),
            onError: (error) => {
              logger.error(error);
              reject(
                intl.formatMessage({
                  defaultMessage:
                    "An error occurred while saving the rules. Please, try again later.",
                }),
              );
            },
          }),
        )
      }
      canUploadFiles
    />
  );
}
const EventRulesPageQuery = graphql`
  query EventRulesPageQuery($slug: String!) {
    viewer @ifAllowed {
      id
    }
    eventBySlug(slug: $slug) {
      id
      title
      latestRule {
        id
        text
      }
      viewerCanUpdate: viewerCan(action: UPDATE_EVENT)
      viewerCanAccept: viewerCan(action: CREATE_EVENT_RULE_AGREEMENT)
      ...EventAcceptRulesButtonEventFragment
    }
  }
`;
export const loader = (environment: Environment) => {
  return ({ params: { slug } }: LoaderArgs) => {
    if (!slug) {
      throw new Response("No slug", { status: 500 });
    }
    return preload<EventRulesPageQueryType>(environment, EventRulesPageQuery, {
      slug,
    });
  };
};
const useEventRulesPageQuery = () => usePreloaded<EventRulesPageQueryType>();

export const lazyHandle = {
  titleI18n: defineMessage({ defaultMessage: "Rules" }),
};
